import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Badge } from '../components/ui/Badge';
import { Shield, CheckCircle, FileText, Globe, User, Settings, Bell, CreditCard, Lock, X, LogOut, ChevronRight, ExternalLink, RefreshCw, Key, Download, Trash2, AlertTriangle, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import toast from 'react-hot-toast';
import { UserProfile } from '@clerk/react';
import { usePassport, useUserPreferences, useUpdateUserPreferences, useVerifyPassport } from '../hooks/usePassport';
import { SHOULD_USE_MOCKS } from '../lib/clerk';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker, getNotifications, markAllNotificationsAsRead } from '../api/worker';

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, memberships = [], signOut } = useCurrentUser();
  const { switchOrg } = useTenant();
  const [activeTab, setActiveTab] = useState('Global Passport');
  const [status, setStatus] = useState('ONLINE');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  
  const { data: passport, isLoading: isPassportLoading } = usePassport();
  const { data: preferences, isLoading: isPrefsLoading } = useUserPreferences();
  const updatePrefs = useUpdateUserPreferences();
  const verifyPassport = useVerifyPassport();
  const queryClient = useQueryClient();

  const { data: notificationsData, isLoading: isNotificationsLoading } = useQuery({
    queryKey: ['my-notifications'],
    queryFn: () => getNotifications(0, 50),
  });

  const notificationsObj = notificationsData || { content: [] };
  const notifications = Array.isArray(notificationsObj) ? notificationsObj : notificationsObj.content || [];

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      toast.success('All notifications marked as read');
    },
    onError: () => toast.error('Failed to clear notifications')
  });

  const exportDataMutation = useMutation({
    mutationFn: async () => {
      const response = await apiWorker.post('/api/users/me/export');
      return response.data;
    },
    onSuccess: () => {
      toast.success('Personal data export initiated. Your archive is being prepared.');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to start personal data export.');
    }
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.delete('/api/users/me');
    },
    onSuccess: () => {
      toast.success('Account deletion requested. Initiating 30-day grace period...');
      setTimeout(() => signOut(), 2000);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to request account deletion.');
    }
  });

  const [privacySettings, setPrivacySettings] = useState({
    hideMobile: false,
    hideEmail: false,
  });

  // Sync state with fetched preferences (render-phase adjustment rather than an effect).
  const [prevPreferences, setPrevPreferences] = useState<typeof preferences | null>(null);
  if (preferences && prevPreferences !== preferences) {
    setPrevPreferences(preferences);
    setPrivacySettings({
      hideMobile: !!preferences.hideMobile,
      hideEmail: !!preferences.hideEmail,
    });
    if (preferences.availabilityStatus) {
      setStatus(preferences.availabilityStatus);
    }
  }

  const { data: recentTransactions = [] } = useQuery({
    queryKey: ['my-transactions'],
    queryFn: async () => {
      try {
        const response = await apiWorker.get('/api/ledger/me/transactions');
        return Array.isArray(response.data) ? response.data : (response.data?.content || []);
      } catch {
        return [];
      }
    }
  });

  const safeMemberships = Array.isArray(memberships) ? memberships : [];
  const safeRecentTransactions = Array.isArray(recentTransactions) ? recentTransactions : [];
  const safePassportCredentials = Array.isArray(passport?.credentials) ? passport.credentials : [];

  const handleTogglePrivacy = (key: keyof typeof privacySettings) => {
    const newValue = !privacySettings[key];
    setPrivacySettings(prev => ({ ...prev, [key]: newValue }));
    
    updatePrefs.mutate({ [key]: newValue }, {
      onSuccess: () => toast.success('Privacy settings updated'),
      onError: () => {
        setPrivacySettings(prev => ({ ...prev, [key]: !newValue })); // Revert on error
        toast.error('Failed to save settings');
      }
    });
  };

  const handleStatusChange = (newStatus: string) => {
    const previousStatus = status;
    setStatus(newStatus);
    
    updatePrefs.mutate({ availabilityStatus: newStatus }, {
      onSuccess: () => toast.success(`Status updated to ${newStatus.replace('_', ' ').toLowerCase()}`),
      onError: () => {
        setStatus(previousStatus); // Revert on error
        toast.error('Failed to update status');
      }
    });
  };

  const handleRequestVerification = () => {
    verifyPassport.mutate(undefined);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      {/* Header Section */}
      <div className="bg-white rounded-none p-8 border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] relative overflow-hidden dark:bg-slate-900">
        <div className="absolute top-0 right-0 p-8 flex items-center gap-4">
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-none border-2 border-black dark:border-white dark:bg-slate-950">
            <div className={`w-2 h-2 rounded-none ${status === 'ONLINE' ? 'bg-green-500' : status === 'BUSY' ? 'bg-amber-500' : 'bg-gray-400'}`} />
            <select 
              value={status} 
              onChange={(e) => handleStatusChange(e.target.value)}
              className="text-[10px] font-bold uppercase tracking-widest bg-transparent outline-none cursor-pointer text-gray-600"
            >
              <option value="ONLINE">Available</option>
              <option value="BUSY">Do Not Disturb</option>
              <option value="OFFLINE">Incognito</option>
            </select>
          </div>
          <Settings
            size={20}
            onClick={() => navigate('/profile/notifications')}
            className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
          />
        </div>
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-24 h-24 rounded-none bg-brand-primary/10 flex items-center justify-center border-4 border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden">
            {user?.imageUrl ? (
              <img src={user.imageUrl} alt={user.fullName || ''} className="w-full h-full object-cover" />
            ) : (
              <User size={40} className="text-brand-primary" />
            )}
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{user?.fullName || 'User'}</h1>
            <p className="text-gray-500 font-mono text-sm uppercase tracking-wider">
              {user?.primaryEmailAddress?.emailAddress || user?.email || (Array.isArray(user?.emailAddresses) ? user?.emailAddresses[0]?.emailAddress : '')}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-3">
              {passport?.status === 'PENDING' ? (
                <Badge variant="amber" className="bg-amber-100 text-amber-700 border-amber-200">
                  <Clock size={10} className="mr-1" /> Verification Under Review
                </Badge>
              ) : passport?.vettingLevel && passport.vettingLevel >= 1 ? (
                <Badge variant="green" className="bg-green-100 text-green-700 border-green-200">
                  <CheckCircle size={10} className="mr-1" /> Level {passport.vettingLevel} Verified
                </Badge>
              ) : (
                <Badge variant="gray" className="bg-gray-100 text-gray-700 border-gray-200">
                  Unverified
                </Badge>
              )}
              <Badge 
                variant={SHOULD_USE_MOCKS ? 'amber' : 'purple'} 
                className={SHOULD_USE_MOCKS ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-purple-100 text-purple-700 border-purple-200'}
              >
                <Key size={10} className="mr-1" />
                Auth: {SHOULD_USE_MOCKS ? 'Local Dev Mocks' : 'Live Clerk Auth'}
              </Badge>
              <Badge variant="blue" className="bg-blue-100 text-blue-700 border-blue-200">
                {safeMemberships.length} active memberships
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left Column: Navigation/Stats */}
        <div className="md:col-span-1 space-y-4">
          <div className="bg-white rounded-none p-2 border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
            {[
              { label: 'Global Passport', icon: Shield },
              { label: 'Memberships', icon: Globe },
              { label: 'Finances', icon: CreditCard },
              { label: 'Notifications', icon: Bell },
              { label: 'Security', icon: Lock },
            ].map((item, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(item.label)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-none transition-all ${
                  activeTab === item.label 
                    ? 'bg-brand-primary text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-brand-primary/20' 
                    : 'text-gray-600 hover:bg-white dark:bg-slate-950'
                }`}
              >
                <item.icon size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            ))}
            <div className="h-px bg-white my-1 mx-2 dark:bg-slate-800" />
            <button
              onClick={() => signOut()}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-none text-red-600 hover:bg-red-50 transition-all font-medium text-sm"
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>

          <div className="bg-white rounded-none p-5 border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4 dark:bg-slate-900">
            <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-widest flex items-center gap-2">
              <Lock size={14} className="text-brand-primary" />
              Privacy Controls
            </h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer group">
                <div className="space-y-0.5">
                  <p className="text-xs font-medium text-gray-700">Hide Mobile #</p>
                  <p className="text-[10px] text-gray-400 leading-tight">Only show to leadership</p>
                </div>
                <div 
                  onClick={() => handleTogglePrivacy('hideMobile')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-none transition-colors group-hover:bg-opacity-80 ${privacySettings.hideMobile ? 'bg-brand-primary' : 'bg-gray-200'}`}
                >
                  <div className={`h-4 w-4 rounded-none bg-white transition-transform transform ${privacySettings.hideMobile ? 'translate-x-[18px]' : 'translate-x-[2px]'} shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]`} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer group">
                <div className="space-y-0.5">
                  <p className="text-xs font-medium text-gray-700">Hide Email</p>
                  <p className="text-[10px] text-gray-400 leading-tight">Restrict to app messaging</p>
                </div>
                <div 
                  onClick={() => handleTogglePrivacy('hideEmail')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-none transition-colors group-hover:bg-opacity-80 ${privacySettings.hideEmail ? 'bg-brand-primary' : 'bg-gray-200'}`}
                >
                  <div className={`h-4 w-4 rounded-none bg-white transition-transform transform ${privacySettings.hideEmail ? 'translate-x-[18px]' : 'translate-x-[2px]'} shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]`} />
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="md:col-span-2 space-y-6">
          <AnimatePresence mode="wait">
            {activeTab === 'Global Passport' && (
              <motion.section
                key="passport"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Shield size={20} className="text-brand-primary" />
                    Global Verification Passport
                  </h2>
                  <button 
                    onClick={handleRequestVerification}
                    disabled={verifyPassport.isPending || passport?.status === 'PENDING'}
                    className="text-xs font-bold text-brand-primary uppercase tracking-widest hover:underline disabled:opacity-50"
                  >
                    {verifyPassport.isPending ? 'Submitting...' : passport?.status === 'PENDING' ? 'Under Review' : 'Request Verification'}
                  </button>
                </div>
                {passport?.status === 'PENDING' && (
                  <div className="p-4 mb-4 bg-amber-50 border-2 border-black dark:border-white border-amber-400 text-amber-900 rounded-none flex items-center gap-3 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
                    <Clock className="text-amber-600 shrink-0" size={20} />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider">Verification Under Review</p>
                      <p className="text-xs text-amber-800 mt-0.5">Your passport and identity verification request is currently under review. High-level permissions will activate once vetted.</p>
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  {isPassportLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="animate-spin text-gray-300" size={24} />
                    </div>
                  ) : (
                    safePassportCredentials.map((cred, idx) => (
                      <motion.div 
                        key={cred.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex items-center justify-between group hover:border-brand-primary/30 transition-all dark:bg-slate-900"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-none bg-white flex items-center justify-center text-gray-400 group-hover:bg-brand-primary/10 group-hover:text-brand-primary transition-all dark:bg-slate-950">
                            <FileText size={20} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{cred.name}</p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-mono uppercase tracking-tight">{cred.issuer}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={cred.status === 'VERIFIED' ? 'green' : 'amber'} className={`border-none px-2 py-0 text-[9px] uppercase tracking-wider font-bold ${cred.status === 'VERIFIED' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                            {cred.status}
                          </Badge>
                          <p className="text-[9px] text-gray-400 mt-1 uppercase font-mono">Expires: {cred.expires}</p>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </motion.section>
            )}

            {activeTab === 'Memberships' && (
              <motion.section
                key="memberships"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Globe size={20} className="text-brand-primary" />
                  Active Memberships
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {safeMemberships.length === 0 ? (
                    <div className="bg-white p-8 rounded-none border-2 border-black dark:border-white border-dashed text-center dark:bg-slate-900">
                      <p className="text-sm text-gray-500">You are not currently enrolled in any organizations.</p>
                      <button
                        type="button"
                        onClick={() => navigate('/create-org')}
                        className="mt-3 px-4 py-2 bg-navy-900 text-white font-bold text-xs uppercase tracking-widest rounded-none shadow-[2px_2px_0px_#000000]"
                      >
                        Create or Join Org
                      </button>
                    </div>
                  ) : (
                    safeMemberships.map((m) => (
                      <div 
                        key={m.id} 
                        onClick={() => m.nodeSlug && switchOrg(m.nodeSlug, '/dashboard')}
                        className="bg-white p-5 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex items-center justify-between dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-none bg-navy-50 flex items-center justify-center text-navy-900 dark:text-white font-bold group-hover:bg-[#FFD600] group-hover:text-black transition-colors">
                            {(m.nodeName || 'O').charAt(0)}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-[#0044FF] transition-colors">{m.nodeName}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="blue" className="text-[10px] py-0 px-2">{m.role}</Badge>
                              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono font-bold uppercase tracking-wider">Active</span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-gray-400 group-hover:translate-x-1 group-hover:text-black dark:group-hover:text-white transition-all" />
                      </div>
                    ))
                  )}
                </div>
              </motion.section>
            )}

            {activeTab === 'Finances' && (
              <motion.section
                key="finances"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <CreditCard size={20} className="text-brand-primary" />
                  Financial Passport
                </h2>
                
                <div className="bg-white border-2 border-black dark:border-white rounded-none p-6 space-y-4 dark:bg-slate-900">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Recent Transactions</h4>
                    {recentTransactions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => navigate('/ledger')}
                        className="text-[10px] font-bold text-brand-primary uppercase hover:underline"
                      >
                        View All
                      </button>
                    )}
                  </div>
                  
                  {safeRecentTransactions.length === 0 ? (
                    <div className="py-8 text-center bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-950">
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No recent transactions found.</p>
                      <p className="text-xs text-gray-400 mt-1">Your ledger is empty.</p>
                    </div>
                  ) : (
                    safeRecentTransactions.slice(0, 5).map((t: any, idx: number) => (
                      <div key={t.id || idx} className="flex justify-between items-center py-3 border-b border-gray-50 last:border-0">
                        <div>
                          <p className="text-xs font-bold text-gray-900 dark:text-white">{t.description || 'Transaction'}</p>
                          <p className="text-[10px] text-gray-400 font-mono">
                            {new Date(t.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`text-xs font-bold font-mono ${t.amount < 0 ? 'text-red-500' : 'text-green-600'}`}>
                          {t.amount < 0 ? '-' : '+'}${Math.abs(t.amount).toFixed(2)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </motion.section>
            )}

            {activeTab === 'Notifications' && (
              <motion.section
                key="notifications"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Bell size={20} className="text-brand-primary" />
                    Notifications
                  </h2>
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => markAllReadMutation.mutate()}
                      disabled={markAllReadMutation.isPending || notifications.length === 0}
                      className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600 disabled:opacity-50"
                    >
                      Clear All
                    </button>
                    <Link
                      to="/profile/notifications"
                      className="text-[10px] font-bold text-brand-primary uppercase tracking-widest hover:underline flex items-center gap-1"
                    >
                      <Settings size={12} />
                      Preferences
                    </Link>
                  </div>
                </div>

                <div className="space-y-2">
                  {isNotificationsLoading ? (
                    <div className="p-4 text-center text-xs text-gray-400 font-mono">Loading notifications...</div>
                  ) : notifications.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400 font-mono border-2 border-black dark:border-white rounded-none bg-white border-dashed dark:bg-slate-950">No notifications</div>
                  ) : (
                    notifications.map((n: any) => {
                      const title = n.title || n.data?.metadata?.subject || 'Notification';
                      return (
                      <div key={n.id} className={`p-4 rounded-none border-2 border-black dark:border-white transition-all ${n.read ? 'bg-white border-black dark:border-white dark:bg-slate-900' : 'bg-blue-50 border-blue-100'}`}>
                        <div className="flex justify-between items-start gap-4">
                          <div className="space-y-1">
                            <h4 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h4>
                            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{n.message}</p>
                          </div>
                          <span className="text-[9px] font-mono text-gray-400 whitespace-nowrap">{new Date(n.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    );
                  })
                  )}
                </div>
              </motion.section>
            )}

            {activeTab === 'Security' && (
              <motion.section
                key="security"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Lock size={20} className="text-brand-primary" />
                    Security & Identity
                  </h2>
                </div>
                
                <div className="bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden p-0 max-w-full clerk-user-profile-container dark:bg-slate-900">
                  {SHOULD_USE_MOCKS ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400 font-medium">
                      Security management is disabled in local development mode. Please switch to live Clerk Auth to manage connected accounts and 2FA.
                    </div>
                  ) : (
                    <div className="flex justify-center py-4 bg-white dark:bg-slate-950">
                      <UserProfile />
                    </div>
                  )}
                </div>

                <div className="mt-8 pt-6 border-t border-red-100 dark:border-red-900/30">
                  <h3 className="text-sm font-bold text-red-600 dark:text-red-500 mb-4 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Danger Zone
                  </h3>
                  
                  <div className="bg-red-50 dark:bg-red-950/10 border-2 border-black dark:border-white border-red-100 dark:border-red-900/30 rounded-none p-6 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">Export Personal Data</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 max-w-lg">
                          Download a complete archive of your personal information, messages, and activity across all organizations.
                        </p>
                      </div>
                      <button 
                        onClick={() => exportDataMutation.mutate()}
                        disabled={exportDataMutation.isPending}
                        className="shrink-0 px-4 py-2 bg-white dark:bg-slate-900 border-2 border-black dark:border-white rounded-none text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {exportDataMutation.isPending ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <Download size={14} />
                        )}
                        {exportDataMutation.isPending ? 'Preparing Export...' : 'Take It With You'}
                      </button>
                    </div>

                    <div className="h-px bg-red-100 dark:bg-red-900/30 w-full" />

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-bold text-red-600 dark:text-red-500">Delete Account Forever</h4>
                        <p className="text-xs text-red-800/70 dark:text-red-400 mt-1 max-w-lg leading-relaxed">
                          Permanently delete your account and all associated personal data. 
                          This action cannot be undone. Financial ledger records will be anonymized to preserve organizational accounting.
                        </p>
                      </div>
                      <button 
                        onClick={() => {
                          setDeleteConfirmInput('');
                          setIsDeleteModalOpen(true);
                        }}
                        disabled={deleteAccountMutation.isPending}
                        className="shrink-0 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-none text-xs font-bold transition-colors flex items-center justify-center gap-2 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-red-600/20 disabled:opacity-50 cursor-pointer"
                      >
                        <Trash2 size={14} />
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>

          <section className="pt-6 border-t border-black dark:border-white">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Globe size={20} className="text-brand-primary" />
              Active Contexts
            </h2>
            <div className="bg-white rounded-none p-6 border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-950">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                Your passport credentials are automatically shared with organizations that require them for your role. You have control over shared visibility.
              </p>
              <div className="flex flex-wrap gap-2">
                {safeMemberships.map((m) => (
                  <div key={m.id} className="bg-white px-3 py-2 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex items-center gap-2 dark:bg-slate-900">
                    <div className="w-2 h-2 rounded-none bg-green-500 animate-pulse" />
                    <span className="text-xs font-bold text-gray-700">{m.nodeName}</span>
                    <span className="text-[10px] text-gray-400 bg-white px-1.5 rounded uppercase font-mono dark:bg-slate-950">{m.role}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
      {/* Account Deletion Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-slate-900 border-4 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle size={24} />
              <h3 className="text-lg font-black uppercase tracking-tight">Permanently Delete Account</h3>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              This action cannot be undone. All personal data will be erased, and you will be immediately signed out. To confirm, type <strong className="font-mono text-black dark:text-white">DELETE</strong> below:
            </p>
            <input
              type="text"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              placeholder="Type DELETE"
              className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-950 font-mono"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 border-black dark:border-white bg-white dark:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteConfirmInput !== 'DELETE' || deleteAccountMutation.isPending}
                onClick={() => {
                  deleteAccountMutation.mutate();
                  setIsDeleteModalOpen(false);
                }}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-red-600 text-white disabled:opacity-40"
              >
                {deleteAccountMutation.isPending ? 'Deleting...' : 'Confirm Deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
